import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, TrendingDown, Car, Home, Layers, Briefcase, Building2, Building, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

const CASES = [
  {
    id: "auto-1940",
    icon: Car,
    eyebrow: "Auto Insurance",
    headline: "$1,940 saved on auto",
    subline: "Same coverage. Same driver. New carrier.",
    accent: "44%",
    story: {
      who: "Marcus T. · Cincinnati, OH",
      vehicle: "2021 Toyota Camry · Single driver · Clean record",
      before: "$287 / month with a captive carrier",
      after: "$125 / month after Knight re-quoted across 6 carriers",
      summary:
        "Marcus had been with the same carrier for nine years and assumed his rate was fair. We re-quoted his auto policy in under 20 minutes, matched his coverage line-for-line, and found a carrier offering the same protection for $162 less per month — $1,940 in annual savings without changing a single deductible or limit.",
      coverage: ["Liability 100/300", "Comp & Collision · $500 ded.", "Uninsured Motorist", "Roadside"],
    },
  },
  {
    id: "bundle-1200",
    icon: Layers,
    eyebrow: "Auto + Home Bundle",
    headline: "Bundled & dropped $1,200",
    subline: "Two policies, one carrier, one annual win.",
    accent: "41%",
    story: {
      who: "The Reilly Family · West Chester, OH",
      vehicle: "Two vehicles + 4-bed colonial home",
      before: "$421 / month across two separate carriers",
      after: "$248 / month bundled with a single A-rated carrier",
      summary:
        "The Reillys had auto with one company and home with another — convenient, but expensive. Knight bundled both policies under one carrier with stronger replacement-cost coverage on the home, kept their auto limits identical, and reduced their combined premium by over $1,200 per year.",
      coverage: ["Auto · Full coverage", "Home · Replacement cost", "$1M Personal Umbrella eligible", "Multi-policy discount"],
    },
  },
  {
    id: "premium-87",
    icon: Home,
    eyebrow: "Homeowners",
    headline: "$87/mo lower premium",
    subline: "Better coverage. Lower monthly.",
    accent: "33%",
    story: {
      who: "Janelle K. · Cincinnati, OH",
      vehicle: "1,950 sq ft single-family · Built 1998",
      before: "$256 / month · ACV roof, $2,500 deductible",
      after: "$169 / month · Replacement-cost roof, $1,000 deductible",
      summary:
        "Janelle's renewal jumped 18% — so she called Knight. We found a carrier that not only beat her old premium by $87/month, but upgraded her roof from actual-cash-value to replacement-cost and dropped her deductible by $1,500. Better coverage. Lower bill. Same address.",
      coverage: ["Dwelling · Replacement cost", "Roof · Replacement cost", "$1,000 deductible", "Water backup included"],
    },
  },
  {
    id: "business-3840",
    icon: Briefcase,
    eyebrow: "Business Insurance",
    headline: "$3,840 saved on BOP",
    subline: "Better liability limits. Lower annual.",
    accent: "37%",
    story: {
      who: "Hendrickson Trade Co. · Cincinnati, OH",
      vehicle: "12-employee HVAC contractor · 2 shop vehicles",
      before: "$865 / month · $1M GL, undersized property limits",
      after: "$545 / month · $2M GL, fully updated property schedule",
      summary:
        "Hendrickson's old Business Owners Policy hadn't been reviewed in five years. Knight pulled fresh quotes from four commercial carriers, doubled the general-liability limit, added employee-dishonesty coverage, and modernized the property schedule — all while shaving $3,840 off the annual premium.",
      coverage: ["$2M General Liability", "Commercial Property", "Hired & Non-Owned Auto", "Employee Dishonesty"],
    },
  },
  {
    id: "commercial-fleet",
    icon: Building2,
    eyebrow: "Commercial Auto",
    headline: "$5,720 saved on fleet",
    subline: "Five trucks. One renewal. Real savings.",
    accent: "29%",
    story: {
      who: "Riverbend Logistics LLC · Northern KY",
      vehicle: "5-truck fleet · 7 drivers · MVR-clean roster",
      before: "$1,640 / month with a single national carrier",
      after: "$1,163 / month with a regional commercial specialist",
      summary:
        "Riverbend's fleet renewal arrived 22% higher than the prior year. Knight re-marketed the package across three commercial-trucking carriers, found one that rewarded their clean MVR roster, and locked in $5,720 of annual savings — with higher cargo limits and an added trailer-interchange endorsement.",
      coverage: ["Commercial Auto Liability", "Physical Damage · all vehicles", "Cargo $100K", "Hired/Non-Owned"],
    },
  },
  {
    id: "condo-bundle",
    icon: Building,
    eyebrow: "Condo + Auto Bundle",
    headline: "Condo bundle saved $1,460",
    subline: "City life, simplified — and discounted.",
    accent: "38%",
    story: {
      who: "Aiden & Sophie · Downtown Cincinnati",
      vehicle: "2-bed condo (HO-6) + two vehicles",
      before: "$338 / month split across two carriers",
      after: "$216 / month bundled with one A-rated carrier",
      summary:
        "Aiden and Sophie had their HO-6 condo policy with one carrier and their two cars with another. Knight rolled both into a single bundled policy, increased their loss-assessment coverage to $50K, and dropped their combined premium by $122 / month — a $1,460 annual win without changing a single limit on the auto side.",
      coverage: ["HO-6 Condo · $50K loss assessment", "Auto · 100/300 limits", "Multi-policy discount", "$1M Umbrella eligible"],
    },
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function CaseStudies() {
  const [openId, setOpenId] = useState(null);
  const [paused, setPaused] = useState(false);
  const active = CASES.find((c) => c.id === openId) || null;
  const scrollerRef = useRef(null);

  const scrollByCards = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85 * dir;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  // Auto-scroll the row continuously until the user interacts or opens a modal
  useEffect(() => {
    if (paused || openId) return;
    const el = scrollerRef.current;
    if (!el) return;
    const id = setInterval(() => {
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: 1.1, behavior: "auto" });
      }
    }, 30);
    return () => clearInterval(id);
    // el, id, atEnd are all local to the effect closure; only paused & openId
    // determine when this should re-run.
  }, [paused, openId]);

  return (
    <section
      id="case-studies"
      className="py-24 sm:py-32 bg-[#E5E5EA]"
      data-testid="case-studies-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <motion.div
          {...fadeUp}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14"
        >
          <div>
            <p className="text-[12px] uppercase tracking-[0.36em] text-[#011233] font-semibold mb-4">
              Mini case studies
            </p>
            <h2 className="font-heading font-bold text-4xl sm:text-6xl text-[#011233] leading-[1.02] tracking-tight">
              Proof, in six stories.
            </h2>
          </div>
          <div className="flex flex-col sm:items-end gap-4">
            <p className="text-base text-slate-600 max-w-md leading-relaxed">
              Real policies. Real numbers. Scroll the row and tap any card to read
              the full re-quote walk-through.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollByCards(-1)}
                aria-label="Previous case studies"
                className="w-11 h-11 rounded-full bg-white text-[#011233] ring-1 ring-[#011233]/15 flex items-center justify-center hover:bg-[#011233] hover:text-white transition-colors"
                data-testid="case-scroll-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => scrollByCards(1)}
                aria-label="Next case studies"
                className="w-11 h-11 rounded-full bg-white text-[#011233] ring-1 ring-[#011233]/15 flex items-center justify-center hover:bg-[#011233] hover:text-white transition-colors"
                data-testid="case-scroll-next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </motion.div>

        <div
          ref={scrollerRef}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          className="kn-case-scroller flex gap-6 overflow-x-auto pb-6 -mx-6 sm:-mx-10 px-6 sm:px-10"
          data-testid="case-scroller"
        >
          {CASES.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.button
                key={c.id}
                type="button"
                onClick={() => setOpenId(c.id)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  ease: [0.22, 1, 0.36, 1],
                  delay: i * 0.06,
                }}
                whileHover={{ y: -8 }}
                className="kn-case-card group relative text-left rounded-[28px] bg-white overflow-hidden ring-1 ring-[#011233]/10 p-8 sm:p-9 flex flex-col flex-shrink-0 w-[300px] sm:w-[340px]"
                data-testid={`case-card-${c.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="inline-flex w-12 h-12 rounded-2xl bg-[#011233]/5 text-[#011233] items-center justify-center transition-all duration-300 group-hover:bg-[#011233] group-hover:text-white">
                    <Icon size={20} strokeWidth={2.2} />
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#011233] text-white text-[11px] font-semibold">
                    <TrendingDown size={12} />
                    {c.accent}
                  </span>
                </div>

                <p className="mt-7 text-[11px] uppercase tracking-[0.22em] text-[#011233] font-semibold">
                  {c.eyebrow}
                </p>
                <h3 className="mt-2 font-heading font-bold text-[#011233] text-2xl sm:text-3xl tracking-tight leading-tight">
                  {c.headline}
                </h3>
                <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
                  {c.subline}
                </p>

                <div className="mt-5 flex gap-2">
                  <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-white" style={{ background: "linear-gradient(135deg, #E63946 0%, #B91C2C 100%)" }}>
                    Before
                  </span>
                  <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-white" style={{ background: "linear-gradient(135deg, #10B981 0%, #047857 100%)" }}>
                    After
                  </span>
                </div>

                <span className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#011233] font-semibold opacity-70 group-hover:opacity-100 transition-opacity">
                  Read the story
                  <ArrowRight
                    size={14}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent
          className="max-w-2xl bg-white border-0 rounded-[24px] p-0 overflow-hidden"
          data-testid="case-study-modal"
        >
          {active && (
            <div className="flex flex-col">
              <div className="bg-[#011233] text-white p-8 sm:p-10">
                <DialogHeader className="space-y-3 text-left">
                  <p className="text-[11px] uppercase tracking-[0.32em] text-[#B5BAC2] font-semibold">
                    {active.eyebrow}
                  </p>
                  <DialogTitle className="font-heading font-bold text-3xl sm:text-4xl tracking-tight leading-tight text-white">
                    {active.headline}
                  </DialogTitle>
                  <DialogDescription className="text-white/80 text-[14px] leading-relaxed">
                    {active.subline}
                  </DialogDescription>
                </DialogHeader>
              </div>

              <div className="p-8 sm:p-10 space-y-6">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#011233] font-semibold">
                    Client
                  </p>
                  <p className="mt-1 text-[15px] text-slate-900">
                    {active.story.who}
                  </p>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    {active.story.vehicle}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #E63946 0%, #B91C2C 100%)" }}>
                    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/15" />
                    <p className="relative text-[10px] uppercase tracking-[0.22em] text-white/90 font-semibold">
                      Before
                    </p>
                    <p className="relative mt-2 text-[14px] leading-relaxed">
                      {active.story.before}
                    </p>
                  </div>
                  <div className="rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #10B981 0%, #047857 100%)" }}>
                    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/15" />
                    <p className="relative text-[10px] uppercase tracking-[0.22em] text-white/90 font-semibold">
                      After Knight
                    </p>
                    <p className="relative mt-2 text-[14px] leading-relaxed">
                      {active.story.after}
                    </p>
                  </div>
                </div>

                <p className="text-[14px] text-slate-700 leading-relaxed">
                  {active.story.summary}
                </p>

                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#011233] font-semibold mb-3">
                    Coverage matched
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {active.story.coverage.map((item) => (
                      <span
                        key={item}
                        className="px-3 py-1.5 rounded-full bg-[#011233]/5 text-[#011233] text-[12px] font-medium"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <a
                  href="#quote"
                  onClick={() => setOpenId(null)}
                  className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#011233] text-white px-6 py-3 text-[14px] font-semibold hover:bg-[#0F2050] transition-colors"
                  data-testid="case-modal-cta"
                >
                  Get my re-quote
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
